import { LevelRange } from "@/components/shared/util";
import Brain from "./brain";
import { BrainText } from "./brainText";
import ChestTube from "./chestTube";
import { CoughText } from "./coughText";
import { EblText } from "./eblText";
import Ett from "./ett";
import Heart from "./heart";
import { HeartText } from "./heartText";
import { HeartVolumeText } from "./heartVolumeText";
import { HeartNeedDataText } from "./heartNeedDataText";
import Icd from "./icd";
import LeftKidney from "./leftKidney";
import { LeftKidneyText } from "./leftKidneyText";
import { LungRateOnlyText } from "./lungRateOnlyText";
import Lungs from "./lungs";
import { LungsText } from "./lungsText";
import Part, { PartBase } from "./part";
import Pump from "./pump";
import { RightKidneyText } from "./rightKidneyText";
import Spine from "./spine";
import Temp from "./temp";
import Ulcer from "./ulcer";
import Uterus from "./uterus";

export interface BodyConfig {
  // Assigned by using svgConfig keys
  key: string;
  // Name of the Part class
  className: typeof PartBase | typeof Part;
  // Atom key to look at for level and risk
  atomId: string;
  popupComponentName: string;
  // SVG group that contains the parts paths, allows for show / hide of entire part
  groupName: string;
  // String that will be found in list of risks
  riskKey: string;
  // SVG id's that should be thin grey or thicker orange for no risk / risk
  riskIds: Array<string>;
  // SVG id's that should be filled with the current level (green/yellow/red)
  levelIds: Array<string>;
  // Hide if no atom present
  hideWithNoAtom?: boolean;
  // Hide if no age or no atom
  hideWithNoAge?: boolean;
}

export interface PartData {
  legend: string;
  risk: boolean;
  level: LevelRange;
  value: number;
}

const svgConfig: Record<string, BodyConfig> = {
  liver: {
    key: "",
    className: Part,
    popupComponentName: "Liver",
    atomId: "liver",
    groupName: "liver",
    riskKey: "Liver",
    riskIds: ["liverOuter"],
    levelIds: ["liverInner"]
  },
  leftKidney: {
    key: "",
    className: LeftKidney,
    popupComponentName: "Urine",
    atomId: "urine",
    groupName: "lKidney",
    riskKey: "Kidney",
    riskIds: ["lKidneyRisk"],
    levelIds: ["lKidneyLevel"]
  },
  rightKidney: {
    key: "",
    className: Part,
    popupComponentName: "Creatinine",
    atomId: "cr",
    groupName: "rKidney",
    riskKey: "Kidney",
    riskIds: ["rKidneyRisk"],
    levelIds: ["rKidneyLevel"]
  },
  uterus: {
    key: "",
    className: Uterus,
    popupComponentName: "Uterus",
    atomId: "gp",
    riskKey: "Uterus",
    groupName: "uterusGroup",
    riskIds: ["prenatalRisk", "postPartumRisk", "placentaPostRisk"],
    levelIds: []
  },
  ulcer: {
    key: "",
    className: Ulcer,
    popupComponentName: "Ulcer",
    atomId: "ulcerWorst",
    riskKey: "None",
    groupName: "ulcerGroup",
    riskIds: [],
    levelIds: []
  },
  brain: {
    key: "",
    className: Brain,
    popupComponentName: "Brain",
    atomId: "", // dynamically set by brain code
    groupName: "brain",
    riskKey: "Brain",
    riskIds: ["brainRisk"],
    levelIds: ["brainLevel", "brainLevelTransparent"]
  },
  heart: {
    key: "",
    className: Heart,
    popupComponentName: "Heart",
    atomId: "volume",
    groupName: "heart",
    riskKey: "Heart",
    riskIds: ["heartRisk"],
    levelIds: ["heartVolume", "heartVolumeTransparent"]
  },
  pump: {
    key: "",
    className: Pump,
    popupComponentName: "Pump",
    atomId: "",
    groupName: "pump",
    riskKey: "",
    riskIds: ["pumpInner", "pumpOuter"],
    levelIds: ["pumpLevel"]
  },
  lungs: {
    key: "",
    className: Lungs,
    popupComponentName: "Lungs",
    atomId: "peep",
    groupName: "lungs",
    riskKey: "None",
    riskIds: [],
    levelIds: []
  },
  temp: {
    key: "",
    className: Temp,
    popupComponentName: "Temperature",
    atomId: "temp",
    groupName: "temp",
    riskKey: "Temperature",
    riskIds: [],
    levelIds: []
  },

  airway: {
    key: "",
    className: Part,
    popupComponentName: "Lungs",
    atomId: "pip",
    groupName: "airway",
    riskKey: "Airway",
    riskIds: [
      "airwayOutlineTopLeft",
      "airwayOutlineBottom",
      "airwayOutlineTopRight"
    ],
    levelIds: ["airwayLevel"]
  },
  spine: {
    key: "",
    className: Spine,
    popupComponentName: "Spine",
    atomId: "antiCoagulant",
    groupName: "spine",
    riskKey: "Spine",
    riskIds: ["spineRisk1", "spineRisk2", "spineRisk3"],
    levelIds: ["spineLevel1", "spineLevel2", "spineLevel3"]
  },
  ett: {
    key: "",
    className: Ett,
    popupComponentName: "Airway",
    atomId: "ett",
    groupName: "ett",
    riskKey: "ETT",
    riskIds: ["lowerMouth", "endoRisk"],
    levelIds: []
  },
  icd: {
    key: "",
    className: Icd,
    popupComponentName: "Lungs",
    atomId: "icd",
    groupName: "icdGroup",
    riskKey: "None",
    riskIds: [],
    levelIds: []
  },
  rightKidneyText: {
    key: "",
    className: RightKidneyText,
    popupComponentName: "Creatinine",
    atomId: "rightKidneyText",
    groupName: "rightKidneyText",
    riskKey: "None",
    riskIds: [],
    levelIds: []
  },
  leftKidneyText: {
    key: "",
    className: LeftKidneyText,
    popupComponentName: "Urine",
    atomId: "leftKidneyText",
    groupName: "leftKidneyText",
    riskKey: "None",
    riskIds: [],
    levelIds: []
  },
  eblText: {
    key: "",
    className: EblText,
    popupComponentName: "Ebl",
    atomId: "ebl",
    groupName: "eblText",
    riskKey: "None",
    riskIds: [],
    levelIds: []
  },
  lungRateOnlyText: {
    key: "",
    className: LungRateOnlyText,
    popupComponentName: "Lungs",
    atomId: "lungRate",
    groupName: "lungsText",
    riskKey: "None",
    riskIds: [],
    levelIds: []
  },
  heartText: {
    key: "",
    className: HeartText,
    popupComponentName: "Heart",
    atomId: "heartText",
    groupName: "heartText",
    riskKey: "None",
    riskIds: [],
    levelIds: []
  },
  heartVolumeText: {
    key: "",
    className: HeartVolumeText,
    popupComponentName: "Heart",
    atomId: "heartVolumeText",
    groupName: "heartVolumeText",
    riskKey: "None",
    riskIds: [],
    levelIds: []
  },
  heartNeedDataText: {
    key: "",
    className: HeartNeedDataText,
    popupComponentName: "Heart",
    atomId: "needData",
    groupName: "heartNeedDataText",
    riskKey: "None",
    riskIds: [],
    levelIds: []
  },
  lungsText: {
    key: "",
    className: LungsText,
    popupComponentName: "Lungs",
    atomId: "lungsText",
    groupName: "lungsText",
    riskKey: "None",
    riskIds: [],
    levelIds: []
  },
  brainText: {
    key: "",
    className: BrainText,
    popupComponentName: "Brain",
    atomId: "brainText",
    groupName: "brainText",
    riskKey: "None",
    riskIds: [],
    levelIds: []
  },
  chestTube: {
    key: "",
    className: ChestTube,
    popupComponentName: "ChestTube",
    atomId: "chestTube",
    groupName: "chestTube",
    riskKey: "None",
    riskIds: [],
    levelIds: ["chestTubeInner", "chestTubeDrop"]
  },
  mouthAndChin: {
    key: "",
    className: Part,
    popupComponentName: "None",
    atomId: "none",
    groupName: "mouthAndChin",
    riskKey: "None",
    riskIds: [],
    levelIds: []
  },
  cough: {
    key: "",
    className: Part,
    popupComponentName: "Cough",
    atomId: "cough",
    groupName: "cough",
    riskKey: "None",
    riskIds: [],
    hideWithNoAge: true,
    levelIds: [
      "coughCrest",
      "coughLine1",
      "coughLine2",
      "coughLine3",
      "coughCircle1",
      "coughCircle2",
      "coughCircle3",
      "coughCircle4"
    ]
  },
  coughText: {
    key: "",
    className: CoughText,
    popupComponentName: "Cough",
    atomId: "cough",
    groupName: "coughText",
    riskKey: "None",
    riskIds: [],
    levelIds: [],
    hideWithNoAge: true
  },
  activity: {
    key: "",
    className: Part,
    popupComponentName: "Activity",
    atomId: "activity",
    groupName: "activity",
    riskKey: "None",
    riskIds: [],
    levelIds: [
      "activityHead",
      "activityBody",
      "activityLine1",
      "activityLine2",
      "activityLine3"
    ]
  }
};

export default svgConfig;
